import { createContext, useContext, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { RealtimeClient } from '@zawarski/palmetto-typescript-data-grid';
import { REALTIME_WS_URL } from '@constants/index';
import { useAppSelector } from '@store/hooks';
import { jwtTokenSelector } from '@store/app/app.selector';
import type { RepRealtimeEvent } from '@/types';

const RealtimeContext = createContext<RealtimeClient | null>(null);

/**
 * One websocket per app, shared by every grid and detail page; opened once the
 * token is known and closed on unmount. Null when no realtime URL is
 * configured (grids then revalidate on their own).
 */
export const RealtimeProvider = ({ children }: { children: ReactNode }) => {
  const idToken = useAppSelector(jwtTokenSelector);
  const client = useMemo(
    () =>
      REALTIME_WS_URL && idToken
        ? new RealtimeClient({ url: REALTIME_WS_URL, token: idToken })
        : null,
    [idToken],
  );
  useEffect(() => () => client?.close(), [client]);
  return <RealtimeContext.Provider value={client}>{children}</RealtimeContext.Provider>;
};

export const useRealtimeClient = () => useContext(RealtimeContext);

/**
 * Local echo of a save/delete: pushes the mutation result through the same
 * pipeline the API's realtime publish would, so the kept-alive grid patches
 * instantly (and still lands when the socket is down).
 */
export const useRealtimeEcho = () => {
  const client = useRealtimeClient();
  return useMemo(
    () => ({
      row: (
        channel: string,
        type: 'insert' | 'update',
        row: { pvDataID: number } & Record<string, unknown>,
      ) =>
        client?.injectLocal(channel, {
          v: 1,
          type,
          id: row.pvDataID,
          row,
          ts: new Date().toISOString(),
        } satisfies RepRealtimeEvent),
      remove: (channel: string, id: number) =>
        client?.injectLocal(channel, { v: 1, type: 'delete', id, ts: new Date().toISOString() }),
      refresh: (channel: string) =>
        client?.injectLocal(channel, { v: 1, type: 'refresh', ts: new Date().toISOString() }),
    }),
    [client],
  );
};

/**
 * Subscribes a non-grid page (a view page, a lookup list) to a channel and runs
 * `onEvent` for every event. Returns nothing; the subscription follows the
 * component's lifetime.
 */
export const useRealtimeSubscription = (
  channel: string | null,
  onEvent: (event: RepRealtimeEvent) => void,
) => {
  const client = useRealtimeClient();
  useEffect(() => {
    if (!client || !channel) return;
    const subscription = client.subscribe(channel, {
      onMessage: (event) => onEvent(event as RepRealtimeEvent),
    });
    return () => subscription.unsubscribe();
  }, [client, channel, onEvent]);
};
